import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useState } from 'react';

/**
 * El retraso antes de mostrar un esqueleto.
 *
 * Una respuesta que tarda 120 ms con un esqueleto encima se ve peor que sin nada: el esqueleto
 * aparece y desaparece antes de que el ojo lo registre, y eso es un parpadeo, no una transición.
 * Por debajo de este umbral no se muestra nada y la pantalla simplemente aparece.
 */
const RETRASO_MS = 260;

/**
 * Cuánto tiene que quedarse un esqueleto una vez que apareció.
 *
 * Sin esto, una respuesta que llega a los 280 ms produce el mismo parpadeo que se quiso evitar:
 * el esqueleto alcanza a pintarse y se va enseguida. Si se mostró, se queda un momento.
 */
const PERMANENCIA_MS = 420;

/**
 * Decide si corresponde mostrar el esqueleto, con retraso a la entrada y permanencia a la salida.
 */
export function useEsqueleto(cargando: boolean): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (cargando) {
      const entrada = setTimeout(() => setVisible(true), RETRASO_MS);
      return () => clearTimeout(entrada);
    }

    if (!visible) {
      return undefined;
    }

    const salida = setTimeout(() => setVisible(false), PERMANENCIA_MS);
    return () => clearTimeout(salida);
  }, [cargando, visible]);

  return visible;
}

/**
 * Un hueso. No lleva texto ni se lee: la etiqueta la pone el contenedor, una sola vez.
 *
 * Un lector de pantalla que anuncia catorce cajas vacías es peor que uno que dice «Cargando el
 * día» y se calla.
 */
export function Hueso({
  ancho = '100%',
  alto = 12,
  radio,
  estilo,
}: {
  ancho?: number | string;
  alto?: number | string;
  radio?: number | string;
  estilo?: CSSProperties;
}) {
  return (
    <span
      className="skeleton"
      aria-hidden="true"
      style={{ width: ancho, height: alto, borderRadius: radio, ...estilo }}
    />
  );
}

/**
 * El contenedor de un esqueleto: es quien habla.
 *
 * `aria-busy` le dice a la tecnología asistiva que acá hay algo en camino, y la etiqueta —la
 * misma que llevaba el spinner que esto reemplaza— dice qué.
 */
export function Esqueleto({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="skeleton-block" role="status" aria-busy="true" aria-label={etiqueta}>
      {children}
    </div>
  );
}

/**
 * El total del día. Sigue la forma de <c>DayTotalCard</c>: cifra grande, unidad, línea de
 * explicación, barra y los tres macros.
 *
 * Que la forma coincida no es prolijidad: un esqueleto que anticipa otra cosa hace que el
 * contenido real empuje la pantalla cuando llega, que es exactamente el salto que se quiere
 * evitar.
 */
export function EsqueletoTotalDelDia() {
  return (
    <Esqueleto etiqueta="Cargando el día">
      <div className="card">
        <Hueso ancho={92} alto={11} />
        <div className="row" style={{ alignItems: 'flex-end', gap: 6 }}>
          <Hueso ancho={148} alto={34} />
          <Hueso ancho={30} alto={13} estilo={{ marginBottom: 4 }} />
        </div>
        <Hueso alto={11} />
        <Hueso ancho="72%" alto={11} />
        <Hueso alto={10} radio="var(--radius-pill)" estilo={{ marginTop: 4 }} />
        <div className="row" style={{ gap: 'var(--space-4)', marginTop: 4 }}>
          {[0, 1, 2].map((indice) => (
            <span key={indice} className="stack" style={{ flex: 1, gap: 6 }}>
              <Hueso alto={3} />
              <Hueso ancho="70%" alto={10} />
              <Hueso ancho="52%" alto={15} />
            </span>
          ))}
        </div>
      </div>
    </Esqueleto>
  );
}

/**
 * Una comida con sus ítems. La regla de 2 px que abre la tarjeta se dibuja igual: es estructura,
 * no contenido, y sacarla haría que la pantalla se reacomode cuando llega lo real.
 */
export function EsqueletoComida({ items = 2 }: { items?: number }) {
  return (
    <div className="meal-card" aria-hidden="true">
      <header className="meal-head">
        <span className="stack" style={{ gap: 6 }}>
          <Hueso ancho={86} alto={12} />
          <Hueso ancho={58} alto={10} />
        </span>
        <Hueso ancho={72} alto={14} />
      </header>
      <div className="stack" style={{ gap: 0 }}>
        {Array.from({ length: items }, (_, indice) => (
          <span key={indice} className="ledger-row">
            <span className="ledger-main">
              <Hueso ancho={indice % 2 === 0 ? '64%' : '48%'} alto={12} />
              <Hueso ancho={indice % 2 === 0 ? '38%' : '30%'} alto={10} />
            </span>
            <Hueso ancho={52} alto={13} />
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Las comidas del día.
 *
 * Una sola tarjeta por omisión, y no dos, porque la cantidad no se sabe antes de leer: con dos,
 * un día vacío terminaba 145 px más corto que su esqueleto y la pantalla se encogía al llegar.
 * Una sola anticipa que viene una lista sin afirmar cuántas.
 */
export function EsqueletoComidas({ comidas = 1 }: { comidas?: number }) {
  return (
    <Esqueleto etiqueta="Cargando las comidas">
      <div className="stack">
        {Array.from({ length: comidas }, (_, indice) => (
          <EsqueletoComida key={indice} items={indice === 0 ? 2 : 3} />
        ))}
      </div>
    </Esqueleto>
  );
}

/**
 * Una lista de filas: historial, búsqueda de productos, mediciones.
 *
 * Los anchos alternan a propósito. Todas las filas del mismo largo se leen como una tabla vacía;
 * alternadas se leen como texto que todavía no llegó, que es lo que son.
 */
export function EsqueletoLista({
  filas = 4,
  etiqueta,
  conValor = true,
}: {
  filas?: number;
  etiqueta: string;
  conValor?: boolean;
}) {
  const anchos = ['68%', '52%', '74%', '46%', '60%'];

  return (
    <Esqueleto etiqueta={etiqueta}>
      <div className="card">
        {Array.from({ length: filas }, (_, indice) => (
          <span key={indice} className="ledger-row">
            <span className="ledger-main">
              <Hueso ancho={anchos[indice % anchos.length]} alto={12} />
              <Hueso ancho="34%" alto={10} />
            </span>
            {conValor && <Hueso ancho={48} alto={13} />}
          </span>
        ))}
      </div>
    </Esqueleto>
  );
}

/** El detalle de un ítem: cifra grande, confianza, macros y el bloque de acciones. */
export function EsqueletoDetalle({ etiqueta = 'Cargando el ítem' }: { etiqueta?: string }) {
  return (
    <Esqueleto etiqueta={etiqueta}>
      <div className="card">
        <Hueso ancho={104} alto={11} />
        <Hueso ancho="58%" alto={18} />
        <Hueso ancho={132} alto={30} />
        <Hueso alto={11} />
        <Hueso ancho="64%" alto={11} />
      </div>
      <div className="card">
        <div className="row" style={{ gap: 'var(--space-4)' }}>
          {[0, 1, 2].map((indice) => (
            <span key={indice} className="stack" style={{ flex: 1, gap: 6 }}>
              <Hueso alto={3} />
              <Hueso ancho="70%" alto={10} />
              <Hueso ancho="52%" alto={15} />
            </span>
          ))}
        </div>
      </div>
    </Esqueleto>
  );
}
